import {
  Users,
  FolderOpen,
  Package,
  ShoppingCart,
  Calendar,
  UserCog,
  Warehouse,
  PackageCheck,
  FileText,
  Wrench,
  Shield,
  Clock,
  Bell,
  AlarmClock,
  CalendarHeart,
  ClipboardPen,
} from 'lucide-react';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme, type ThemeColors } from '@elka/theme';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { DesktopHeader } from '@elka/ui';
import { useIsSuperAdmin } from '@/features/auth/hooks/useIsAdmin';
import { MobileMenu } from './MobileMenu';
import { SectionSwitcher } from './SectionSwitcher';

interface HeaderProps {
  user?: {
    email: string | null;
    displayName: string | null;
    photoURL: string | null;
  };
  onLogout?: () => void;
}

const getNavItems = (
  colors: ThemeColors,
  section: 'projects' | 'rechesh' | 'admin',
  isSuperAdmin: boolean
) => {
  if (section === 'projects') {
    return [
      { to: '/projects/clients', icon: Users, label: 'לקוחות', color: colors.navClients },
      { to: '/projects/list', icon: FolderOpen, label: 'פרויקטים', color: colors.navProjects },
      { to: '/projects/sidur', icon: Calendar, label: 'סידור עבודה', color: colors.navSidur },
      {
        to: '/projects/daily-reports',
        icon: Package,
        label: 'יומני עבודה',
        color: 'from-orange-400 to-amber-500',
      },
      { to: '/projects/toolbox', icon: Wrench, label: 'ארגז כלים', color: colors.navToolbox },
    ];
  }

  // Admin section navigation
  if (section === 'admin') {
    return [
      {
        to: '/admin/vacation-requests',
        icon: Calendar,
        label: 'חופשה',
        color: 'from-blue-400 to-cyan-500',
      },
      ...(isSuperAdmin
        ? [
            {
              to: '/admin/payslips',
              icon: FileText,
              label: 'תלושים',
              color: 'from-green-400 to-emerald-500',
            },
          ]
        : []),
      {
        to: '/admin/time-reports',
        icon: Clock,
        label: 'שעות',
        color: 'from-purple-400 to-violet-500',
      },
      {
        to: '/admin/notifications',
        icon: Bell,
        label: 'הודעות',
        color: 'from-yellow-400 to-orange-500',
      },
      {
        to: '/admin/workers',
        icon: UserCog,
        label: 'עובדים',
        color: 'from-indigo-400 to-blue-500',
      },
      {
        to: '/admin/reminder-settings',
        icon: AlarmClock,
        label: 'תזכורות',
        color: 'from-amber-400 to-orange-500',
      },
      {
        to: '/admin/holidays',
        icon: CalendarHeart,
        label: 'חגים',
        color: 'from-rose-400 to-pink-500',
      },
      ...(isSuperAdmin
        ? [
            {
              to: '/admin/audit-logs',
              icon: Shield,
              label: 'ביקורת',
              color: 'from-red-400 to-pink-500',
            },
          ]
        : []),
    ];
  }

  // Rechesh section navigation
  return [
    { to: '/rechesh/orders', icon: ShoppingCart, label: 'הזמנות רכש', color: colors.navOrders },
    {
      to: '/rechesh/supplier-bids',
      icon: FileText,
      label: 'הצעות מחיר רכש',
      color: 'from-amber-400 to-orange-500',
    },
    { to: '/rechesh/suppliers', icon: Package, label: 'ספקים', color: colors.navSuppliers },
    { to: '/rechesh/supplies', icon: Warehouse, label: 'מלאי', color: colors.navSuppliers },
    {
      to: '/rechesh/goods-receiving',
      icon: PackageCheck,
      label: 'קבלת סחורה',
      color: 'from-green-400 to-emerald-500',
    },
    {
      to: '/rechesh/purchase-requests',
      icon: ClipboardPen,
      label: 'בקשות רכש',
      color: 'from-amber-400 to-orange-500',
    },
  ];
};

export function Header({ user, onLogout }: HeaderProps) {
  const { colors } = useTheme();
  const location = useLocation();

  // Determine current section from URL
  const currentSection = location.pathname.startsWith('/admin')
    ? 'admin'
    : location.pathname.startsWith('/rechesh')
      ? 'rechesh'
      : 'projects';

  const isSuperAdmin = useIsSuperAdmin();
  const navItems = getNavItems(colors, currentSection, isSuperAdmin);

  const isMobile = useIsMobile();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <>
      <DesktopHeader
        title="מערכת ניהול אלקה"
        mobileTitle="אלקה חשמל"
        subtitle="ניהול חכם ומתקדם לפרויקטים שלך"
        logoHref="/projects/clients"
        navItems={navItems.map((item) => ({ ...item, end: true }))}
        user={user ? { displayName: user.displayName, photoURL: user.photoURL } : null}
        onLogout={onLogout}
        onMobileMenuOpen={isMobile ? () => setIsMobileMenuOpen(true) : undefined}
        navPrefix={
          <>
            <SectionSwitcher />
            <div className="h-8 w-px bg-white/20" />
          </>
        }
      />

      {/* Mobile Menu */}
      {isMobile && (
        <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
      )}
    </>
  );
}
